// engine-exception: the reports are read-only folds over metric views (/v1/reports/:name) with no backing table or meta, so the DataGrid is fed columns inferred from each keyed rowset — one screen, driven by the report name; no ScreenDef kind renders a resource-less rowset
import { useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState, ErrorBanner, Money, PageHeader, Skeleton, Tabs, humanize, useApiQuery } from '../../engine';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { formatMinutes, formatQty } from '../../lib/format';

// Reports: profitability, utilization and balances over the metric views. One screen renders
// any /v1/reports/:name as a table whose columns come from the rows' keys — money keys
// (*_minor) format through formatMoney, *_qty through formatQty, minutes as h:mm.

const REPORTS = [
  { name: 'client-profitability', label: 'Client profitability' },
  { name: 'ticket-profitability', label: 'Ticket profitability' },
  { name: 'sla-delivery', label: 'SLA delivery' },
  { name: 'prepay-balances', label: 'Prepay balances' },
  { name: 'ar-balances', label: 'AR balances' },
];

type Row = Record<string, unknown>;

function columnsFor(rows: Row[]): GridColumn<Row>[] {
  const keys = rows.length ? Object.keys(rows[0]!) : [];
  return keys
    .filter((k) => k !== 'currency')
    .map((k) => {
      const align = k.endsWith('_minor') || k.endsWith('_qty') || k === 'minutes' ? ('right' as const) : ('left' as const);
      return {
        id: k,
        header: humanize(k.replace(/_minor$|_qty$/, '')),
        align,
        cell: (r: Row) => renderCell(k, r),
      };
    });
}

function renderCell(key: string, row: Row): ReactNode {
  const v = row[key];
  if (key.endsWith('_minor')) return <Money minor={v as string} currency={(row.currency as string) ?? 'USD'} color={key.includes('margin') || key.includes('balance')} />;
  if (key.endsWith('_qty')) return <span className="hf-num">{formatQty(v as string)}</span>;
  if (key === 'minutes') return <span className="hf-num">{formatMinutes(Number(v))}</span>;
  if (key.endsWith('_id')) return <span className="hf-mono hf-muted">{String(v).slice(0, 8)}</span>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return v == null ? <span className="hf-muted">—</span> : String(v);
}

export function ReportScreen() {
  const [sp, setSp] = useSearchParams();
  const active = REPORTS.find((r) => r.name === sp.get('report')) ?? REPORTS[0]!;
  const q = useApiQuery<{ data: Row[] }>(['reports', active.name], `/v1/reports/${active.name}`);
  const rows = useMemo(() => q.data?.data ?? [], [q.data]);
  const columns = useMemo(() => columnsFor(rows), [rows]);

  return (
    <div className="hf-page">
      <PageHeader title="Reports" subtitle="Profitability, utilization and balances" />
      <Tabs
        variant="pills"
        value={active.name}
        onChange={(name) => setSp((p) => { const n = new URLSearchParams(p); n.set('report', name); return n; }, { replace: true })}
        aria-label="Report"
        tabs={REPORTS.map((r) => ({ key: r.name, label: r.label }))}
      />
      <div className="hf-tabpanel" role="tabpanel">
        {q.isLoading ? (
          <Skeleton lines={6} />
        ) : q.error ? (
          <ErrorBanner error={q.error} />
        ) : (
          <DataGrid<Row>
            aria-label={active.label}
            columns={columns}
            rows={rows}
            keyOf={(r) => String(r.id ?? r.ticket_id ?? r.client_id ?? r.billing_party_id ?? r.prepay_account_id ?? JSON.stringify(r))}
            hideToolbar
            empty={<EmptyState title={`No ${active.label.toLowerCase()} yet`} icon="bar-chart" />}
          />
        )}
      </div>
    </div>
  );
}
